import React, { FC, useEffect, useRef } from "react";

import { Box, Skeleton, Stack } from "@mui/material";

import { TPlayerProps } from "@components/BottomPlayer/components/types";
import { getSkeleton, TSkeletons } from "@components/BottomPlayer/utils";
import { ERoutes } from "@utils/constants";
import { formatTimePlayer } from "@utils/formatTimePlayer";
import styled from "styled-components";

import { AccountType, useAccountType } from "@/contexts/AccountTypeContext";
import { colors } from "@/styles/design-system";

/**
 * MobileLayout — phone + tablet bottom player. New glass-pill design that
 * mirrors DesktopLayout 1:1 (same play / heart / buy buttons), in two rows:
 *   row 1 — cover · title/artist · heart · buy
 *   row 2 — play · time · thin seek line · duration
 * No waveform on phone — a thin seek line instead (the wavesurfer engine stays
 * mounted off-screen so audio + seeking still work, driven via progressRef).
 *
 * Swiping the pill down closes it (stop + clear, like the desktop ×). No corner
 * X — nothing overhangs. Replaces the old responsive layout entirely.
 */

// -- Off-screen wavesurfer host: keeps audio + seek alive while we show a line --
const HiddenWave = styled.div`
  position: fixed;
  left: -9999px;
  top: 0;
  width: 320px;
  height: 36px;
  pointer-events: none;
  opacity: 0;
`;

// -- Glass pill (matches DesktopLayout's DesignOverlay) --
const MobilePill = styled.div`
  position: relative;
  width: 100%;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  padding: 17px 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 11px;
  touch-action: none;
  will-change: transform;
`;

// -- Swipe-to-close grab handle --
const Grab = styled.div`
  position: absolute;
  top: 7px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 4px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.3);
  cursor: grab;
`;

// -- Thin seek line (replaces the waveform on phone) --
// The RAIL is a tall, thumb-friendly hit area (≈ the play button height) so
// the position is easy to grab and drag; the visible line is a thin TRACK
// centered inside it.
const SeekRail = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  height: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
  touch-action: none;
`;

const SeekTrack = styled.div`
  position: relative;
  width: 100%;
  height: 5px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.22);
`;

const SeekFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0%;
  border-radius: 5px;
  background: #f8d6a7;
`;

const SeekThumb = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translate(50%, -50%);
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #f8d6a7;
  box-shadow: 0 0 6px rgba(248, 214, 167, 0.6);
`;

// -- Play button (DesktopLayout PlayBtn 1:1) --
const PlayBtn = styled.button<{ $playing?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ $playing }) => ($playing ? "#ffdca8" : "transparent")};
  border: 1.5px solid ${({ $playing }) => ($playing ? "#ffdca8" : "rgba(255, 255, 255, 0.2)")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $playing }) => ($playing ? "#5a3d2a" : "rgba(255, 255, 255, 0.6)")};
  transition: all 0.2s ease;
  flex-shrink: 0;
  ${({ $playing }) => $playing && "box-shadow: 0 0 12px rgba(255, 220, 168, 0.4);"}

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
    stroke: currentColor;
    stroke-width: 1.5;
  }
`;

// -- Heart button (DesktopLayout HeartBtn 1:1) --
const HeartBtn = styled.button<{ $active?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #ffffff;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
    fill: ${({ $active }) => ($active ? "#ffffff" : "transparent")};
    stroke: #ffffff;
    stroke-width: 1.5;
    transition: all 0.2s ease;
  }
`;

// -- Buy / download (DesktopLayout ActionBtn 1:1) --
const ActionBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.12);
  color: ${colors.accent.blue};
  transition: all 0.2s ease;
  z-index: 0;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    fill: transparent;
    stroke-width: 1.5;
    position: relative;
    z-index: 1;
    transition: stroke 0.2s ease;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: #9cd3fd;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 0;
  }

  &:active::before { opacity: 1; }
  &:active svg { stroke: #0a1a2a; }
`;

// -- Typography (matches DesktopLayout) --
const TrackTitle = styled.span`
  color: #fbf4ec;
  font-family: "Cormorant Garamond", serif;
  font-size: 1.1rem;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
`;

const ComposerName = styled.span`
  color: #efd0b8;
  font-family: "Raleway", sans-serif;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.2;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TimeLabel = styled.span`
  color: rgba(251, 244, 236, 0.5);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.78rem;
  white-space: nowrap;
  min-width: 34px;
  text-align: center;
`;

const Cover = styled.img`
  border-radius: 8px;
  width: 44px;
  height: 44px;
  object-fit: cover;
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
`;

export const MobileLayout: FC<TPlayerProps & { curated?: boolean }> = ({
  state,
  bottomTrackData,
  handleNavigate,
  handleNavigateComposer,
  handleDownload,
  onPlayClick,
  currentTimeRef,
  progressRef,
  containerRef,
  handleLike,
  handleSeek,
  handleCloseTrack,
  curated,
}) => {
  // Entitlements, not plan identity: an account awaiting reactivation is on
  // the subscription plan but is not paying for it, so it browses and
  // downloads on free terms until it reactivates (ToU 17.4).
  const { entitlementType: accountType } = useAccountType();
  const trackTier = (bottomTrackData?.tier || "premium") as "free" | "premium";
  // Premium tracks always show Buy for Free users (re-buyable licenses); owning
  // one doesn't flip to Download — downloads live on the orders page.
  const showDownloadIcon =
    accountType === AccountType.SUBSCRIPTION ||
    accountType === AccountType.MUSIC_CREATOR ||
    (accountType === AccountType.FREE && trackTier === "free");

  const composerDisplay = state.isLoading
    ? null
    : bottomTrackData?.composer
      ? typeof bottomTrackData.composer === "object"
        ? (bottomTrackData.composer as any).name
        : bottomTrackData.composer
      : "";

  const pillRef = useRef<HTMLDivElement>(null);
  const seekRef = useRef<HTMLDivElement>(null);

  // -- Swipe the pill down → close (stop + clear) --
  useEffect(() => {
    const pill = pillRef.current;
    if (!pill) return;
    let startY = 0;
    let dy = 0;
    let dragging = false;

    const move = (e: PointerEvent) => {
      if (!dragging) return;
      dy = Math.max(0, e.clientY - startY);
      if (dy > 4) e.preventDefault();
      pill.style.transform = `translateY(${dy}px)`;
      pill.style.opacity = String(Math.max(0.3, 1 - dy / 200));
    };
    const end = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      if (!dragging) return;
      dragging = false;
      pill.style.transition = "transform 0.28s ease, opacity 0.28s ease";
      if (dy > 80) {
        pill.style.transform = "translateY(140%)";
        pill.style.opacity = "0";
        setTimeout(() => handleCloseTrack?.(), 220);
      } else {
        pill.style.transform = "translateY(0)";
        pill.style.opacity = "1";
      }
    };
    const begin = (e: PointerEvent) => {
      // let taps/drags on controls behave normally (buttons, links, the seek line)
      const t = e.target as HTMLElement;
      if (t.closest("button, a, .bp-seek")) return;
      startY = e.clientY;
      dy = 0;
      dragging = true;
      pill.style.transition = "none";
      window.addEventListener("pointermove", move, { passive: false });
      window.addEventListener("pointerup", end);
    };

    pill.addEventListener("pointerdown", begin);
    return () => {
      pill.removeEventListener("pointerdown", begin);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
    };
  }, [handleCloseTrack]);

  // -- Seek by tapping / dragging the thin line --
  const handleSeekPointer = (e: React.PointerEvent) => {
    const rail = seekRef.current;
    if (!rail) return;
    const apply = (clientX: number) => {
      const r = rail.getBoundingClientRect();
      const p = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
      if (progressRef?.current) progressRef.current.style.width = `${p * 100}%`;
      handleSeek?.(p);
    };
    apply(e.clientX);
    const mv = (ev: PointerEvent) => apply(ev.clientX);
    const up = () => {
      window.removeEventListener("pointermove", mv);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", mv);
    window.addEventListener("pointerup", up);
  };

  return (
    <>
      {/* off-screen waveform engine — drives playback + seek */}
      <HiddenWave>
        <Box id="waveform" ref={containerRef} width="100%" height="100%" />
      </HiddenWave>

      <MobilePill ref={pillRef}>
        <Grab />

        {/* Row 1 — cover · title/artist · heart · buy. Fixed min-height (= the
            44px cover) so the pill never changes height between the loading
            skeletons and the loaded content. */}
        <Stack direction="row" alignItems="center" spacing="12px" sx={{ minHeight: "44px" }}>
          {state.isLoading ? (
            // 44×44 to match <Cover> exactly (the shared AlbumCover skeleton is
            // 48px — it's also used by desktop, so we size it locally here).
            <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: "8px", flexShrink: 0 }} />
          ) : (
            <Cover
              onClick={() => handleNavigate(ERoutes.Album, bottomTrackData?.album.slug, true)}
              alt="Album cover"
              src={bottomTrackData?.smallCoverUrl || bottomTrackData?.largeCoverUrl}
            />
          )}

          <Stack spacing="2px" sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
            <TrackTitle
              className="song-title"
              onClick={() => handleNavigate(ERoutes.Track, bottomTrackData?.slug)}
            >
              {state.isLoading ? getSkeleton(TSkeletons.TITLE) : bottomTrackData?.name}
            </TrackTitle>
            <ComposerName onClick={() => handleNavigateComposer(composerDisplay as string)}>
              {state.isLoading ? getSkeleton(TSkeletons.COMPOSER) : composerDisplay}
            </ComposerName>
          </Stack>

          {!curated && (
            <Stack direction="row" spacing="8px" sx={{ flexShrink: 0 }}>
              <HeartBtn $active={state.isFavorite} onClick={handleLike}>
                <svg viewBox="0 0 24 24">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </HeartBtn>

              <ActionBtn onClick={handleDownload}>
                {showDownloadIcon ? (
                  <svg viewBox="0 0 24 24">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24">
                    <circle cx="9" cy="20.5" r="1.3" fill="none" /><circle cx="17.4" cy="20.5" r="1.3" fill="none" /><path d="M2 3h3l2.4 12.1a1.7 1.7 0 0 0 1.7 1.4h8.5a1.7 1.7 0 0 0 1.7-1.4L21 7.5H6" />
                  </svg>
                )}
              </ActionBtn>
            </Stack>
          )}
        </Stack>

        {/* Row 2 — play · time · thin seek line · duration. Fixed min-height
            (= the 40px play button / seek rail) for the same no-jump reason. */}
        <Stack direction="row" alignItems="center" spacing="11px" sx={{ minHeight: "40px" }}>
          {state.isLoading ? (
            getSkeleton(TSkeletons.PlayButton, true)
          ) : (
            <PlayBtn $playing={state.isPlaying} onClick={onPlayClick}>
              {state.isPlaying ? (
                <svg viewBox="0 0 24 24">
                  <rect x="6" y="4" width="4" height="16" fill="currentColor" />
                  <rect x="14" y="4" width="4" height="16" fill="currentColor" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" style={{ transform: "translateX(1px)" }}>
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              )}
            </PlayBtn>
          )}

          <TimeLabel ref={currentTimeRef as React.Ref<HTMLSpanElement>}>
            {state.isLoading ? getSkeleton(TSkeletons.TIME) : "00:00"}
          </TimeLabel>

          <SeekRail ref={seekRef} className="bp-seek" onPointerDown={handleSeekPointer}>
            <SeekTrack>
              <SeekFill ref={progressRef as React.Ref<HTMLDivElement>}>
                <SeekThumb />
              </SeekFill>
            </SeekTrack>
          </SeekRail>

          <TimeLabel>
            {state.isLoading ? getSkeleton(TSkeletons.TIME) : formatTimePlayer(bottomTrackData?.length)}
          </TimeLabel>
        </Stack>
      </MobilePill>
    </>
  );
};
